import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Tooltip,
  Alert,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TablePaginationSection from './TablePaginationSection';
import { ColumnType, DataTableProps } from './DataTable.types';
import CellEditor from './CellEditor';

const DataTable = <T,>({
  columns,
  data,
  tableLayout = 'auto',
  isLoading,
  isError,
  errorMessage,
  onEdit,
  onInlineSave,
  getRowId,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  selected,
  onSelectionChange = () => {},
  editingRowId,
  setEditingRowId,
  editedRow,
  setEditedRow,
  emptyMessage,
  renderCustomActions,
  highlightedRowId,
  isRowSelectable,
  getRowSx,
}: DataTableProps<T>) => {
  // Jeśli nie przekazano propsa "selected", używamy wewnętrznego stanu.
  const [internalSelected, setInternalSelected] = useState<number[]>([]);
  const effectiveSelected = selected !== undefined ? selected : internalSelected;
  const [localRows, setLocalRows] = useState<T[]>([]);
  const [inlineSaveError, setInlineSaveError] = useState<string>('');
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const lastScrolledHighlightedRowId = useRef<number | null>(null);

  const getInlineSaveErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    const possibleError = error as any;
    if (typeof possibleError?.data === 'string') {
      return possibleError.data;
    }

    if (possibleError?.data?.message) {
      return String(possibleError.data.message);
    }

    if (possibleError?.data?.errors) {
      const all = Object.values(possibleError.data.errors).flat().map(String);
      if (all.length > 0) {
        return all.join(' ');
      }
    }

    return 'Nie udało się zapisać zmian.';
  };

  useEffect(() => {
    if (data) setLocalRows(data);
  }, [data]);

  useEffect(() => {
    if (highlightedRowId == null) {
      lastScrolledHighlightedRowId.current = null;
      return;
    }

    const highlightedRow = rowRefs.current[highlightedRowId];
    if (!highlightedRow || lastScrolledHighlightedRowId.current === highlightedRowId) {
      return;
    }

    highlightedRow.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
    lastScrolledHighlightedRowId.current = highlightedRowId;
  }, [highlightedRowId, localRows]);

  const handleSelectionChange = (newSelected: number[]) => {
    if (selected !== undefined) {
      onSelectionChange && onSelectionChange(newSelected);
    } else {
      setInternalSelected(newSelected);
      onSelectionChange && onSelectionChange(newSelected);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" align="center">
        {errorMessage || 'Wystąpił błąd podczas ładowania danych.'}
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return <Typography align="center">{emptyMessage}</Typography>;
  }

  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const selectedIndex = effectiveSelected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = effectiveSelected.concat(id);
    } else if (selectedIndex === 0) {
      newSelected = effectiveSelected.slice(1);
    } else if (selectedIndex === effectiveSelected.length - 1) {
      newSelected = effectiveSelected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = effectiveSelected
        .slice(0, selectedIndex)
        .concat(effectiveSelected.slice(selectedIndex + 1));
    }

    handleSelectionChange(newSelected);
  };

  const isSelected = (id: number) => effectiveSelected.indexOf(id) !== -1;

  return (
    <Paper sx={{ p: 3 }}>
      {inlineSaveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {inlineSaveError}
        </Alert>
      )}
      <TableContainer>
        <Table sx={{ width: '100%', tableLayout }}>
          <TableHead>
            <TableRow>
              {selected && (
                <TableCell padding="checkbox" align="center">
                  Wybierz
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={String(col.key)}
                  align="center"
                  sx={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </TableCell>
              ))}
              {(onEdit || onInlineSave) && <TableCell align="center">Akcje</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {localRows.map((row) => {
              const id = getRowId(row);
              const isHighlighted = highlightedRowId === id;
              const isSelectableRow = !isRowSelectable || isRowSelectable(row);
              const customRowSx = getRowSx?.(row);
              const rowSx = isHighlighted
                ? {
                    ...(customRowSx || {}),
                    '& > .MuiTableCell-root': {
                      ...((customRowSx?.['& > .MuiTableCell-root'] as Record<string, unknown>) || {}),
                      backgroundColor: 'rgba(255, 193, 7, 0.12)',
                    },
                    '& > .MuiTableCell-root:first-of-type': {
                      ...((customRowSx?.['& > .MuiTableCell-root:first-of-type'] as Record<string, unknown>) ||
                        {}),
                      borderLeft: '4px solid',
                      borderLeftColor: 'warning.main',
                    },
                  }
                : customRowSx;

              return (
                <TableRow
                  key={id}
                  ref={(element) => {
                    if (element) {
                      rowRefs.current[id] = element;
                      return;
                    }

                    delete rowRefs.current[id];
                  }}
                  selected={isSelected(id)}
                  hover
                  data-row-id={id}
                  data-testid={isHighlighted ? 'data-table-highlighted-row' : undefined}
                  sx={rowSx}
                >
                  {selected && (
                    <TableCell padding="checkbox">
                      {isSelectableRow && (
                        <Checkbox
                          checked={isSelected(id)}
                          onChange={(event) => handleCheckboxClick(event, id)}
                        />
                      )}
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={String(col.key)}
                      sx={col.width ? { width: col.width } : undefined}
                    >
                      {col.editable && editingRowId === id ? (
                        <CellEditor
                          type={col.type ?? ColumnType.text}
                          value={
                            editedRow && editedRow[col.key as keyof T] !== undefined
                              ? editedRow[col.key as keyof T]
                              : row[col.key as keyof T]
                          }
                          onChange={(val: any) => {
                            setEditedRow &&
                              setEditedRow({
                                ...(editedRow || {}),
                                [col.key as string]: val,
                              });
                          }}
                          {...Object.fromEntries(
                            Object.entries(col).filter(([key]) => key !== 'onChange')
                          )}
                        />
                      ) : col.render ? (
                        col.render(row)
                      ) : (
                        String((row as any)[col.key] ?? '')
                      )}
                    </TableCell>
                  ))}

                  <TableCell>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
                      {/* Tryb edycji inline */}
                      {onInlineSave &&
                        (editingRowId === id ? (
                          <Tooltip title="Zapisz">
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ p: 0.5 }}
                              onClick={async () => {
                                const updatedRow = { ...row, ...editedRow };
                                try {
                                  setInlineSaveError('');
                                  await onInlineSave(updatedRow);
                                  setEditingRowId && setEditingRowId(null);
                                  setEditedRow && setEditedRow({});
                                } catch (saveError) {
                                  setInlineSaveError(getInlineSaveErrorMessage(saveError));
                                }
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Modyfikuj">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  setInlineSaveError('');
                                  setEditingRowId && setEditingRowId(id);
                                  setEditedRow && setEditedRow(row);
                                }}
                                disabled={editingRowId !== null}
                              >
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ))}

                      {/* Przejście do pełnej edycji */}
                      {onEdit && isSelectableRow && (
                        <Tooltip title="Edytuj">
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ p: 0.5 }}
                            onClick={() => onEdit(id)}
                            disabled={editingRowId === id}
                          >
                            <EditNoteIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Customowe akcje */}
                      {renderCustomActions && renderCustomActions(row)}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {onPageChange && totalCount !== undefined && (
        <TablePaginationSection
          totalCount={totalCount}
          page={page ?? 0}
          rowsPerPage={rowsPerPage ?? 10}
          onPageChange={onPageChange}
          onRowsPerPageChange={(newSize) => {
            onRowsPerPageChange?.(newSize);
          }}
        />
      )}
    </Paper>
  );
};

export default DataTable;
